import React from 'react';
import { usePortalLanguage } from '../../../contexts/PortalLanguageContext';
import AgentHeadshot from './AgentHeadshot';
import {
    buildAgentVCard,
    buildKeyDatesIcs,
    buildPortalCalendarEvents,
    downloadTextFile
} from '../../../utils/portalExport';

const sanitizeFilename = (value) => (value || 'transaction').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase() || 'transaction';

function ContactRow({ label, children }) {
    return (
        <div>
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--sidebar-text-color)] opacity-90">{label}</div>
            <div className="mt-1 break-words">{children}</div>
        </div>
    );
}

function PortalAgentCard({ agentContact, transaction, addressLine, workspaceName, isCompactLayout }) {
    const { t } = usePortalLanguage();
    const name = agentContact?.name || t('Your agent');
    const phone = agentContact?.phone || '';
    const email = agentContact?.email || '';
    const telHref = phone ? `tel:${phone.replace(/[^0-9+]/g, '')}` : '';
    const smsHref = phone ? `sms:${phone.replace(/[^0-9+]/g, '')}` : '';

    const handleSaveContact = () => {
        const vcf = buildAgentVCard({ name, phone, email, workspaceName });
        downloadTextFile(`${sanitizeFilename(name)}.vcf`, 'text/vcard', vcf);
    };

    // Key-date titles follow the portal language; custom milestone titles pass through.
    const events = buildPortalCalendarEvents(transaction || {}).map(event => ({ ...event, title: t(event.title) }));
    const handleAddCalendar = () => {
        const ics = buildKeyDatesIcs(events, { calendarName: addressLine || t('Transaction') });
        downloadTextFile(`${sanitizeFilename(addressLine)}-key-dates.ics`, 'text/calendar', ics);
    };

    const linkClass = 'inline-flex min-h-[44px] items-center font-semibold text-[color:var(--sidebar-text-color)] underline decoration-[color:var(--color-accent)] decoration-2 underline-offset-4 hover:bg-[color:var(--sidebar-item-hover)] rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-accent)]';
    const btnClass = 'w-full min-h-[44px] rounded-md border border-[color:var(--sidebar-border-color)] bg-[color:var(--sidebar-item-hover)] px-3 py-2 text-sm font-semibold text-[color:var(--sidebar-text-color)] hover:bg-[color:var(--sidebar-item-active)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-accent)]';

    return (
        <div
            data-testid="portal-agent-card"
            style={{ backgroundImage: 'var(--gradient-sidebar)' }}
            className={`rounded-2xl border border-[color:var(--sidebar-border-color)] text-[color:var(--sidebar-text-color)] shadow-md ${isCompactLayout ? 'p-5' : 'p-6'}`}
        >
            <div className="flex flex-col items-center text-center">
                <AgentHeadshot url={agentContact?.headshotUrl || ''} name={name} size={96} />
                <h2 className="mt-4 text-lg font-semibold text-[color:var(--sidebar-text-color)]">{name}</h2>
            </div>
            <div className="mt-4 space-y-3 text-sm text-[color:var(--sidebar-text-color)]">
                <ContactRow label={t('Phone')}>
                    {phone ? (
                        <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            <a href={telHref} className={linkClass}>{phone}</a>
                            <a href={smsHref} className={linkClass}>{t('Text')}</a>
                        </span>
                    ) : t('Not provided')}
                </ContactRow>
                <ContactRow label={t('Email')}>
                    {email ? <a href={`mailto:${email}`} className={linkClass}>{email}</a> : t('Not provided')}
                </ContactRow>
            </div>
            <div className="mt-5 space-y-2 border-t border-[color:var(--sidebar-border-color)] pt-5">
                {(phone || email) && (
                    <button type="button" onClick={handleSaveContact} className={btnClass}>{t('Save contact')}</button>
                )}
                {events.length > 0 && (
                    <button type="button" onClick={handleAddCalendar} className={btnClass}>{t('Add key dates to calendar')}</button>
                )}
            </div>
        </div>
    );
}

export default PortalAgentCard;
